"use client";

import { useState } from "react";

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, X } from "lucide-react";
import { useTranslations } from "next-intl";

import { MintButton } from "@/components/shared/mint-button";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";

import { useSaveDashboardLayout } from "../hooks/use-dashboard-prefs";
import { orderWidgets } from "../utils/order-widgets";

import type { DashboardLayout, WidgetDefinition, WidgetSlot } from "@/types";

// Rendered top to bottom in the sheet, matching the dashboard's own layout.
const SLOT_ORDER: WidgetSlot[] = ["header", "grid"];

function WidgetRow({
  widget,
  hidden,
  onToggle,
  reorderLabel,
  toggleLabel,
}: {
  widget: WidgetDefinition;
  hidden: boolean;
  onToggle: () => void;
  reorderLabel: string;
  toggleLabel: string;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: widget.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        "group flex items-center gap-2.5 rounded-2xl border px-2 py-2 transition-colors",
        hidden
          ? "border-transparent bg-surface-tab-inactive/15"
          : "border-border/40 bg-surface-tab-inactive/30",
        isDragging && "border-border shadow-lg",
      )}
    >
      {/* Only the handle starts a drag (touch-none), so the list still scrolls. */}
      <button
        type="button"
        aria-label={reorderLabel}
        className="flex size-9 shrink-0 cursor-grab touch-none items-center justify-center rounded-lg text-muted-foreground/50 outline-none transition-colors hover:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring/50 active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-5" />
      </button>

      <div className="flex min-w-0 flex-1 flex-col">
        <span
          className={cn(
            "truncate text-sm font-medium transition-colors",
            hidden ? "text-muted-foreground/70" : "text-foreground",
          )}
        >
          {widget.title}
        </span>
        <span className="truncate text-xs text-fg-muted">{widget.group}</span>
      </div>

      {/* Never blocked by the slot cap: switching more on than the row fits is
          fine, the first N in this order are the ones displayed. */}
      <Switch
        checked={!hidden}
        onCheckedChange={onToggle}
        aria-label={toggleLabel}
      />
    </li>
  );
}

/**
 * Add / remove / reorder the dashboard. Mirrors the bottom-nav customizer:
 * commit-style draft state, dnd-kit for ordering, Apply persists.
 *
 * The list shows every widget the user MAY see, hidden ones included, and Apply
 * writes back the full order. That's what lets the backend treat "absent from
 * order" as "shipped since the user last looked here", rather than "removed".
 */
export function DashboardCustomizeSheet({
  widgets,
  layout,
  slotLimits,
}: {
  widgets: WidgetDefinition[];
  layout: DashboardLayout;
  slotLimits: Record<WidgetSlot, number | null>;
}) {
  const t = useTranslations("Dashboard");
  const tPage = useTranslations("Page");
  const [open, setOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const save = useSaveDashboardLayout();

  // Draft state — edits stay local until Apply; closing any other way discards
  // them. Re-seeded from the persisted layout each time the sheet opens.
  const [draftOrder, setDraftOrder] = useState<string[]>(layout.order);
  const [draftHidden, setDraftHidden] = useState<string[]>(layout.hidden);

  const ordered = orderWidgets(widgets, draftOrder);

  function handleOpenChange(next: boolean) {
    if (next) {
      setDraftOrder(layout.order);
      setDraftHidden(layout.hidden);
    }
    setOpen(next);
  }

  function toggleHidden(key: string) {
    setDraftHidden((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }

  async function handleApply() {
    await save.mutateAsync({
      // Always the FULL displayed order, not just the reordered part.
      order: ordered.map((w) => w.key),
      hidden: draftHidden,
      params: layout.params,
    });
    setOpen(false);
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const keys = ordered.map((w) => w.key);
    const oldIndex = keys.indexOf(String(active.id));
    const newIndex = keys.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    setDraftOrder(arrayMove(keys, oldIndex, newIndex));
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => handleOpenChange(true)}
        className="rounded-full border-border-soft"
      >
        {t("customize")}
      </Button>

      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          side={isDesktop ? "right" : "bottom"}
          showCloseButton={false}
          className={cn(
            "flex flex-col gap-0 bg-surface-main p-0",
            isDesktop
              ? "h-full w-1/2 max-w-none sm:max-w-none"
              : "inset-0 top-0 h-dvh w-full rounded-none",
          )}
          // Don't auto-focus the first grip handle — it would paint a focus ring
          // and make one row look pre-selected.
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <SheetHeader className="shrink-0 gap-1 px-6 pb-2 pt-[calc(env(safe-area-inset-top)+1.5rem)]">
            <div className="flex items-center justify-between">
              <SheetTitle className="font-display text-2xl font-normal text-foreground">
                {t("customize")}
              </SheetTitle>
              <SheetClose asChild>
                <button
                  type="button"
                  className="flex size-10 items-center justify-center rounded-full bg-surface-tab-inactive text-fg-muted transition-colors hover:bg-surface-tab-inactive/70"
                  aria-label={tPage("close")}
                >
                  <X className="size-5" />
                </button>
              </SheetClose>
            </div>
            <SheetDescription>{t("customizeHint")}</SheetDescription>
          </SheetHeader>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              {/* One SortableContext over ALL keys, so a drag can move a widget
                  anywhere in the order even though the list is shown grouped. */}
              <SortableContext
                items={ordered.map((w) => w.key)}
                strategy={verticalListSortingStrategy}
              >
                <div className="flex flex-col gap-6">
                  {SLOT_ORDER.map((slot) => {
                    const rows = ordered.filter((w) => w.slot === slot);
                    if (rows.length === 0) return null;

                    const limit = slotLimits[slot];

                    return (
                      <section key={slot} className="flex flex-col gap-2">
                        <div className="flex items-baseline justify-between gap-2">
                          <h4 className="text-xs font-semibold uppercase tracking-wider text-fg-muted">
                            {t(`slot_${slot}`)}
                          </h4>
                          {limit !== null && (
                            <span className="text-xs text-fg-muted">
                              {t("slotFirstShown", { limit })}
                            </span>
                          )}
                        </div>

                        <ul className="flex flex-col gap-2">
                          {rows.map((widget) => (
                            <WidgetRow
                              key={widget.key}
                              widget={widget}
                              hidden={draftHidden.includes(widget.key)}
                              onToggle={() => toggleHidden(widget.key)}
                              reorderLabel={t("customizeReorder", {
                                label: widget.title,
                              })}
                              toggleLabel={t("customizeToggle", {
                                label: widget.title,
                              })}
                            />
                          ))}
                        </ul>
                      </section>
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          </div>

          <div className="flex shrink-0 items-center gap-3 border-t border-border-soft/30 px-6 py-5 pb-[calc(env(safe-area-inset-bottom)+1.25rem)]">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDraftOrder([]);
                setDraftHidden([]);
              }}
              className="h-[50px] flex-1 rounded-full border-border-soft text-base font-normal"
            >
              <X className="mr-2 size-4" />
              {t("customizeReset")}
            </Button>
            <MintButton
              type="button"
              onClick={handleApply}
              disabled={save.isPending}
              className="h-[50px] flex-1 text-base"
            >
              {tPage("apply")}
            </MintButton>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
